// packages/ui/src/forms/_i18n/renderFieldError.ts
import type { TFunc } from '@tourtask/types/i18n';
import { translateUnknownKey, translateUnknownKeyOrNull } from '@tourtask/i18n/openKeys';
import type { AnyNamespaceTranslator } from './FormI18nContext';

/** A translator that can also answer "is this a real key?" (next-intl's `t` can). */
type MaybeProbing = AnyNamespaceTranslator | (AnyNamespaceTranslator & { has(key: string): boolean });

function canProbe(t: MaybeProbing): t is TFunc & { has(key: string): boolean } {
  return typeof (t as { has?: unknown }).has === 'function';
}

/**
 * Turn a react-hook-form field error into displayable text.
 *
 * Eleven byte-identical copies of this lived in the `Form*` field components. They are
 * now one, per the repo's no-duplication rule — a fix applied to one copy silently missed
 * the other ten.
 *
 * The awkward part is real, not incidental: RHF types `fieldState.error.message` as
 * `string`, but our Zod schemas put a CATALOG KEY there (`message: 'ERROR_REQUIRED'`), so
 * this is the seam where an untyped channel meets a typed translator. Two paths:
 *
 *  - The translator can probe (`useTranslations()` can): resolve the key properly, and if
 *    the message turns out NOT to be a key, show it verbatim — some schemas legitimately
 *    carry literal copy.
 *  - It cannot (embed's `getStr` before it gains `has`): fall back to the same behaviour
 *    the eleven copies had.
 *
 * With no translator at all the raw message is genuinely all there is; that is a missing
 * `FormI18nProvider`, and returning the code makes it visible rather than blank.
 */
export function renderFieldError(msg: unknown, t?: MaybeProbing): string | undefined {
  if (msg == null) return undefined;
  const s = (typeof msg === 'string' ? msg : '').trim();
  if (!s) return undefined;
  if (!t) return s;

  // `translateUnknownKeyOrNull` owns the one guarded cast, inside `packages/i18n`. A
  // message that is NOT a key comes back `null` and is shown verbatim — some schemas
  // legitimately carry literal copy.
  if (canProbe(t)) return translateUnknownKeyOrNull(t, s) ?? s;

  // No membership probe: the schemas are the only guarantee that `s` is a key, so this
  // keeps the behaviour the eleven copies had.
  return translateUnknownKey(withAlwaysHas(t), s);
}

/** Wraps a probe-less translator so the shared bridge can be used with it. `has` answers
 *  true because there is nothing to ask; the translator's own miss behaviour then applies. */
function withAlwaysHas(t: MaybeProbing): TFunc & { has(key: string): boolean } {
  return Object.assign(t as TFunc, { has: () => true });
}
